import { Checkbox, ComboBox, IDropdownOption, Stack, TextField } from '@fluentui/react';
import { ColumnDto, DataverseConfiguration, DataverseIntegration } from '../../../../../../../api/generated/data-contracts';
import { Dispatch, SetStateAction, useRef } from 'react';
import { DATAVERSE_PROJECTTEAMRESOURCE_COLUMNS, DESELECT_KEY, PROJECT_TEAM_COLUMNS } from '../../../../../../../constants/queryKeys';
import produce from 'immer';
import { useQuery } from '@tanstack/react-query';
import { ApiCalls } from '../../../../../../../api/api';
import { onRenderOption } from '../../../components/ComboBoxTooltip';
import { GetHostName } from '../../../../../../../helpers/trimUrl';

interface IProjectTeamPaneProps {
    config: DataverseConfiguration;
    dataverseTables: IDropdownOption<ColumnDto>[];
    setIntegration: Dispatch<SetStateAction<DataverseIntegration>>;
}

export const ProjectTeamPane = (props: IProjectTeamPaneProps) => {
    const { dataverseUrl, projectTeamTableLogicalName, projectTeamResourceTableLogicalName } = props.config;

    const defaultColumns = useRef([] as IDropdownOption<ColumnDto>[]).current;
    const { data: entityOptions = defaultColumns } = useQuery({
        queryKey: [PROJECT_TEAM_COLUMNS, dataverseUrl, projectTeamTableLogicalName],
        queryFn: () =>
            ApiCalls.dataverseEnvironmentsTablesColumnsList({
                environmentUrl: GetHostName(dataverseUrl),
                tableName: projectTeamTableLogicalName,
            }).then(
                res =>
                    [
                        { key: DESELECT_KEY, text: 'Not selected', data: { logicalName: '' } },
                        ...res.data.map(
                            column => ({ key: column.logicalName, text: column.displayName || column.logicalName, data: column } as IDropdownOption<ColumnDto>),
                        ),
                    ] as IDropdownOption<ColumnDto>[],
            ),
        enabled: Boolean(dataverseUrl && projectTeamTableLogicalName),
        suspense: true,
    });

    const { data: azureIdColumnOptions = defaultColumns } = useQuery({
        queryKey: [DATAVERSE_PROJECTTEAMRESOURCE_COLUMNS, dataverseUrl, projectTeamResourceTableLogicalName],
        queryFn: () =>
            ApiCalls.dataverseEnvironmentsTablesColumnsList({
                environmentUrl: GetHostName(dataverseUrl),
                tableName: projectTeamResourceTableLogicalName,
            }).then(
                res =>
                    [
                        { key: DESELECT_KEY, text: 'Not selected', data: { logicalName: '' } },
                        ...res.data.map(
                            column => ({ key: column.logicalName, text: column.displayName || column.logicalName, data: column } as IDropdownOption<ColumnDto>),
                        ),
                    ] as IDropdownOption<ColumnDto>[],
            ),
        enabled: Boolean(dataverseUrl && projectTeamResourceTableLogicalName),
        suspense: true,
    });

    const taskAssignDropdownOptions: IDropdownOption[] = [
        { key: 'AllTasks', text: 'All Tasks' },
        { key: 'ProjectSummaryTask', text: 'Project Summary Task' },
    ];

    return (
        <Stack tokens={{ childrenGap: 5 }}>
            <ComboBox
                label="Project Team Table"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={projectTeamTableLogicalName ? '' : 'Table required'}
                options={props.dataverseTables}
                onChange={(e, opt, index, value) => {
                    if (opt) {
                        props.setIntegration(
                            produce(integration => {
                                integration.configuration.projectTeamTableLogicalName = opt.key as string;
                                integration.configuration.projectTeamTableLogicalCollectionName = opt.data.logicalCollectionName;
                            }),
                        );
                    } else if (!value) {
                        props.setIntegration(
                            produce(integration => {
                                integration.configuration.projectTeamTableLogicalName = '';
                                integration.configuration.projectTeamTableLogicalCollectionName = '';
                            }),
                        );
                    }
                }}
                selectedKey={projectTeamTableLogicalName ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Team Id Column"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={props.config.projectTeamIdColumn ? '' : 'Team Id column required'}
                options={entityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectTeamIdColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={entityOptions.length <= 0}
                selectedKey={props.config.projectTeamIdColumn}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Project Id Column"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={props.config.projectTeamProjectIdColumn ? '' : 'Project Id column is required'}
                options={entityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectTeamProjectIdColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={entityOptions.length <= 0}
                selectedKey={props.config.projectTeamProjectIdColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Resource Id Column"
                allowFreeform
                autoComplete="on"
                required
                errorMessage={props.config.projectTeamResourceIdColumn ? '' : 'Resource Id Column is required'}
                options={entityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectTeamResourceIdColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={entityOptions.length <= 0}
                selectedKey={props.config.projectTeamResourceIdColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Create assignments on"
                allowFreeform
                autoComplete="on"
                options={taskAssignDropdownOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectTeamAllTasksOrProjectSummaryTask = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={entityOptions.length <= 0}
                selectedKey={props.config.projectTeamAllTasksOrProjectSummaryTask ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Closed Column"
                allowFreeform
                autoComplete="on"
                options={entityOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectTeamClosedColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={entityOptions.length <= 0}
                selectedKey={props.config.projectTeamClosedColumn ?? ''}
                onRenderOption={onRenderOption}
            />
            <TextField
                label={'Closed Compare Value'}
                onChange={(e, value) =>
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectTeamClosedValue = value ?? '';
                        }),
                    )
                }
                value={props.config.projectTeamClosedValue ?? ''}
                disabled={entityOptions.length <= 0}
            />
            <Checkbox
                label="Use Custom Resource Azure Id Column"
                onChange={(e, checked) =>
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectTeamResourceAzureIdAlternateColumnEnabled = checked;
                        }),
                    )
                }
                checked={props.config.projectTeamResourceAzureIdAlternateColumnEnabled}
            />
            <ComboBox
                label="Resource table"
                allowFreeform
                autoComplete="on"
                options={props.dataverseTables}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectTeamResourceTableLogicalName = (opt?.key as string) || '';
                            integration.configuration.projectTeamResourceTableLogicalCollectionName = (opt?.data?.logicalCollectionName as string) || '';
                        }),
                    );
                }}
                disabled={entityOptions.length <= 0}
                selectedKey={props.config.projectTeamResourceTableLogicalName ?? ''}
                onRenderOption={onRenderOption}
            />
            <ComboBox
                label="Azure Id Column"
                allowFreeform
                autoComplete="on"
                options={azureIdColumnOptions}
                onChange={(e, opt, index, value) => {
                    props.setIntegration(
                        produce(integration => {
                            integration.configuration.projectTeamResourceAzureIdColumn = (opt?.key as string) || value || '';
                        }),
                    );
                }}
                disabled={entityOptions.length <= 0}
                selectedKey={props.config.projectTeamResourceAzureIdColumn ?? ''}
                onRenderOption={onRenderOption}
            />
        </Stack>
    );
};
